import { useState, useCallback, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import Auth from "../services/auth";
import Forms from "../services/forms";
import { useQuery } from "../common/Hooks";
import LandingPage from "./components/LandingPage";
import TextBox from "../components/controls/form/TextBox";
import Checkbox from "../components/controls/form/Checkbox";

const StartTrial = () => {
    const query = useQuery();
    const [translate] = useTranslation();

    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [trialAgreement, setTrialAgreement] = useState(null);

    const getAgreement = useCallback(async () => {
        const res = await Forms.getTrialAgreement();

        if (!res.error) {
            setTrialAgreement(res.data.trialAgreement);
        }
    }, []);

    const startTrial = useCallback(async ({ email, phone, organisation, domain, firstName, lastName }) => {
        setIsLoading(true);
        const res = await Auth.startTrial({ email, phone, organisation, domain, firstName, lastName });
        setIsLoading(false);

        if (!res.error) {
            setResetEmailSent(true);
        }
    }, []);

    useEffect(() => {
        getAgreement();
    }, [getAgreement]);

    const trialAgreementLabel = useMemo(() => {
        if (!trialAgreement) {
            return null;
        }
        return (
            <span>{translate("OTM_START_TRIAL_AGREEMENT_1")} <a href={trialAgreement} target="_blank" rel="noopener noreferrer">{translate("OTM_START_TRIAL_AGREEMENT_2")}</a></span>
        );
    }, [trialAgreement, translate]);

    const renderStartTrialForm = useMemo(() => {
        if (isLoading) {
            return <>
                <div className="col-12 mt-2 d-flex flex-column align-items-center">
                    <div className="spinner-border" />
                    <span className="mt-2 fw-bold">{translate("OTM_STARTING_TRIAL_MESSAGE")}</span>
                </div>
            </>
        }

        return <>
            <div className="col-12 mt-2 text-center">
                {translate("OTM_START_TRIAL_CONFIRMATION")}
            </div>
            <Formik
                initialValues={{
                    email: query.get("email") || window.localStorage.getItem("email") || "",
                    phone: query.get("phone") || "",
                    organisation: query.get("organisation") || "",
                    domain: query.get("domain") || "",
                    firstName: query.get("firstName") || "",
                    lastName: query.get("lastName") || "",
                    hasAgreedToTerms: false
                }}
                validationSchema={Yup.object({
                    email: Yup.string()
                        .email(translate("OTM_INVALID_EMAIL"))
                        .required(" "),
                    organisation: Yup.string()
                        .required(" "),
                    domain: Yup.string()
                        .required(" "),
                    firstName: Yup.string()
                        .required(" "),
                    lastName: Yup.string()
                        .required(" "),
                    hasAgreedToTerms: Yup.bool().oneOf([true])
                })}
                onSubmit={startTrial}
                validateOnMount={true}
            >
                {(props) => (
                    <Form className="col-12 mt-2">
                        <div className="row g-2">
                            <div className="col-6">
                                <TextBox
                                    label="OTM_START_TRIAL_EMAIL"
                                    type="email"
                                    name="email"
                                    isRequired={true}
                                />
                            </div>
                            <div className="col-6">
                                <TextBox
                                    label="OTM_START_TRIAL_PHONE"
                                    type="phone"
                                    name="phone"
                                />
                            </div>
                            <div className="col-6">
                                <TextBox
                                    label="OTM_START_TRIAL_ORGANISATION"
                                    name="organisation"
                                    isRequired={true}
                                />
                            </div>
                            <div className="col-6">
                                <TextBox
                                    label="OTM_START_TRIAL_DOMAIN"
                                    tooltip={translate("OTM_DOMAIN_NAME_TOOLTIP")}
                                    buttonTooltip={true}
                                    name="domain"
                                    isRequired={true}
                                />
                            </div>
                            <div className="col-6">
                                <TextBox
                                    label="OTM_START_TRIAL_FIRST_NAME"
                                    name="firstName"
                                    isRequired={true}
                                />
                            </div>
                            <div className="col-6">
                                <TextBox
                                    label="OTM_START_TRIAL_LAST_NAME"
                                    name="lastName"
                                    isRequired={true}
                                />
                            </div>
                            <div className="col-12">
                                <Checkbox
                                    label="OTM_START_TRIAL_AGREEMENT_FULL"
                                    labelComponent={trialAgreementLabel}
                                    name="hasAgreedToTerms"
                                    isRequired={true}
                                    disabled={!trialAgreement}
                                />
                            </div>
                            <div className="col-12 mt-2 text-center">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="mx-auto"
                                    disabled={!props.isValid}
                                >
                                    {translate("OTM_START_TRIAL_SUBMIT")}
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    }, [isLoading, query, startTrial, trialAgreementLabel, trialAgreement, translate]);

    return (
        <LandingPage>
            <div className="row mt-n2">
                {resetEmailSent ? (
                    <div className="col-12 mt-2 text-center">
                        {translate("OTM_START_TRIAL_SENT")}
                    </div>
                ) :
                    renderStartTrialForm
                }
                {!isLoading && <div className="col-12 mt-2">
                    <Link to="/auth">
                        {translate("OTM_START_TRIAL_RETURN_TO_LOGIN")}
                    </Link>
                </div>}
            </div >
        </LandingPage >
    );
};

export default StartTrial;
